import type { Camera, Reachability, ReachabilityReason, StreamStatus } from "../../lib/api";
import { isServerLive } from "./serverHealth";

/**
 * Whether a camera can be reached, in operator words — a different question
 * from describeServerHealth, which only says whether the SERVER is pulling its
 * stream. A camera nobody is watching is always "Not running" there, whether
 * it is up or down; this answers the question the picker actually has.
 */
export interface ReachabilityPresentation {
  kind: Reachability;
  /** The StatusDot colour. */
  variant: "success" | "error" | "neutral";
  /** Short, always-safe-to-render label. */
  label: string;
  /** One curated sentence — never anything the server sent as free text. */
  tooltip: string;
}

const TOOLTIP_BY_REASON: Record<ReachabilityReason, string> = {
  channel_offline: "The recorder reports this camera as disconnected from it.",
  recorder_unreachable: "The recorder is not answering, so none of its cameras can be reached.",
  recorder_auth_failed:
    "The recorder rejected the saved login, so its cameras cannot be checked. An administrator needs to check the recorder's saved login and that it allows Digest authentication.",
  recorder_auth_unavailable:
    "The recorder answered but offers no login this server can use. An administrator needs to enable Digest authentication on the recorder.",
  port_unreachable: "The camera's stream port is not accepting connections.",
  not_checked: "No recent check.",
};

const LABEL_ONLINE = "Online";
const LABEL_OFFLINE = "Offline";
const LABEL_UNKNOWN = "Status unknown";
const UNKNOWN_NO_FEED = "Waiting for the live status feed.";
const ONLINE_STREAMING = "Streaming right now.";
const ONLINE_CHECKED = "Reachable at the last check.";

/**
 * @param status The camera's entry in the health feed, if one has arrived.
 * @param isFeedLive Whether the feed itself is currently live
 *   (`health.phase === "live"`). A snapshot held from before an interruption
 *   is the last thing that was true, not necessarily what is true now, and a
 *   green dot that outlives the connection that justified it is worse than no
 *   dot on a surface whose only job is being trustworthy at a glance.
 */
export function describeReachability(status: StreamStatus | undefined, isFeedLive: boolean): ReachabilityPresentation {
  if (!isFeedLive || !status) {
    return { kind: "unknown", variant: "neutral", label: LABEL_UNKNOWN, tooltip: UNKNOWN_NO_FEED };
  }

  // A stream the server is pulling right now is direct evidence the camera is
  // up; a monitor answer a few seconds older must not contradict it.
  if (isServerLive(status)) {
    return { kind: "online", variant: "success", label: LABEL_ONLINE, tooltip: ONLINE_STREAMING };
  }

  // The reason arrives over the wire, so a value this build does not know (a
  // newer backend's) must still get a tooltip rather than an empty one.
  const reason = (status.reachability_reason && TOOLTIP_BY_REASON[status.reachability_reason]) || TOOLTIP_BY_REASON.not_checked;
  switch (status.reachability) {
    case "online":
      return { kind: "online", variant: "success", label: LABEL_ONLINE, tooltip: ONLINE_CHECKED };
    case "offline":
      return { kind: "offline", variant: "error", label: LABEL_OFFLINE, tooltip: reason };
    default:
      return { kind: "unknown", variant: "neutral", label: LABEL_UNKNOWN, tooltip: reason };
  }
}

/** How many of these cameras are offline right now. Unknown ones are not
 *  counted: "7 offline" must mean seven cameras known to be down, not seven
 *  the monitor could not check. */
export function countOffline(cameras: ReadonlyArray<Pick<Camera, "id">>, byCamera: ReadonlyMap<string, StreamStatus>, isFeedLive: boolean): number {
  return cameras.filter((c) => describeReachability(byCamera.get(c.id), isFeedLive).kind === "offline").length;
}
